import { Box, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import NoBankAccount from '../../assets/images/NoBankAccount';
import { colors } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { ContactSupportModalContent } from '../../components/contact-support/ContactSupportModalContent';
import { AddBankAccountDrawer } from '../../components/make-payment/AddBankAccountDrawer';
import { CommonModal } from '../../components/modal/modal';
import AddPaymentMethodModal from '../../components/modals/AddPaymentMethodModal';
import { CustomMultiSelect } from '../../components/select/CustomMultiSelect';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import CommonHeader from '../../components/ui-components/CommonHeader';
import {
    ACCOUNT_NUMBER_MAX_LENGTH,
    accountNumberValidation,
    bankNameValidation,
    reEnterAccountNumberValidation,
    sanitizeBankNameInput,
} from '../../components/validations/validations';
import {
    BankAccountApiResponse,
    createBankAccount,
    getBankAccountsList,
    updateBankAccountStatus,
} from '../../services/bank-account-services';
import { ContractApiResponse } from '../../services/contract-services';
import {
    createStripeSetupIntent,
    getStripePaymentMethods,
    OpenInvoiceApiResponse,
    StripePaymentMethodItem,
} from '../../services/payment-services';
import { getOpenInvoices } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { trackBankAccountAction, trackBankAccountActionFailed } from '../../utils/google-analytics';
import { canMakePaymentForContract } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { BankAccountCard } from './BankAccountCard';
import { BankAccountCardWrapper, BankAccountsContainer } from './BankAccountStyles';

interface BankAccount {
    id: string;
    accountType: string;
    nickName?: string;
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    accountId: string;
    IsActive?: boolean;
    AspireIsActive?: boolean;
    AutoPay?: boolean;
}

// Utility to map country name to code
const getCountryCode = (country: string) => {
    const trimmed = country.trim();
    if (trimmed === 'United States') return 'USA';
    if (trimmed === 'Canada') return 'CAN';
    return trimmed;
};

type AddAccountFormShape = {
    accountType: string;
    nickName: string;
    bankName: string;
    routingNumber: string;
    reEnterRoutingNumber: string;
    accountNumber: string;
    reEnterAccountNumber: string;
};

function validateRoutingNumber(rn: string, t: (key: string) => string): string {
    const trimmed = rn.trim();
    if (!trimmed) return t('BankAccounts.validation.routingNumberRequired');
    if (!/^\d+$/.test(trimmed)) return t('BankAccounts.validation.routingNumberOnlyNumbers');
    if (trimmed.length !== 9) return t('BankAccounts.validation.routingNumberExactlyNine');
    if (/^0+$/.test(trimmed)) return t('BankAccounts.validation.routingNumberNoZeros');
    if (/^(\d)\1+$/.test(trimmed)) return t('BankAccounts.validation.routingNumberNoSameDigit');
    return '';
}

function validateReEnterRoutingNumber(rrn: string, routingNumber: string, t: (key: string) => string): string {
    const trimmed = rrn.trim();
    if (!trimmed) return t('BankAccounts.validation.reEnterRoutingNumberRequired');
    if (trimmed.length !== 9) return t('BankAccounts.validation.reEnterRoutingNumberExactlyNine');
    if (routingNumber !== rrn) return t('BankAccounts.validation.routingNumberNoMatch');
    return '';
}

// Pay All button: disabled only when open invoices have finished loading AND no contract can make payment.
// During loading (isOpenInvoicesLoading=true) keep button enabled to avoid false-disabled flash.
function calculateAllContractsDisabled(
    isOpenInvoicesLoading: boolean,
    activeContracts: ContractApiResponse[],
    openInvoices: OpenInvoiceApiResponse[],
): boolean {
    if (isOpenInvoicesLoading) return false;
    if (!activeContracts?.length) return true;
    return activeContracts.every(c => !canMakePaymentForContract(c, openInvoices ?? [], activeContracts));
}

function getToggleModalTitle(isMovingToInactive: boolean, t: (key: string) => string): string {
    return isMovingToInactive
        ? t('BankAccounts.modal.deactivateBankAccount')
        : t('BankAccounts.modal.activateBankAccount');
}

// Shared positioning for the centered "empty state" overlay (cards list / accounts list).
const EMPTY_STATE_OVERLAY_SX = {
    position: 'absolute' as const,
    top: { xs: '60%', md: '55%' },
    left: '50%',
    transform: 'translate(-50%, -30%)',
};

function getToggleModalBodyText(isMovingToInactive: boolean): string {
    return isMovingToInactive
        ? 'Are you sure you want to deactivate this bank account?'
        : 'Are you sure you want to activate this bank account?';
}

/** Same rules as submit validation — used to disable Add Account until the form can succeed. */
function computeAddAccountFormErrors(
    formData: AddAccountFormShape,
    country: string,
    t: (key: string) => string,
): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!country || country.trim() === '') errors.country = t('BankAccounts.validation.countryRequired');
    if (!formData.accountType) errors.accountType = t('BankAccounts.validation.accountTypeRequired');

    const bankNameError = bankNameValidation(formData.bankName);
    if (bankNameError) errors.bankName = bankNameError;

    const accountNumberError = accountNumberValidation(formData.accountNumber);
    const isAccountNumberValid = !accountNumberError;
    if (accountNumberError) errors.accountNumber = accountNumberError;

    const reEnterError = reEnterAccountNumberValidation(
        formData.reEnterAccountNumber,
        formData.accountNumber,
        isAccountNumberValid,
    );
    if (reEnterError) errors.reEnterAccountNumber = reEnterError;

    const routingError = validateRoutingNumber(formData.routingNumber, t);
    if (routingError) errors.routingNumber = routingError;

    const reEnterRoutingError = validateReEnterRoutingNumber(formData.reEnterRoutingNumber, formData.routingNumber, t);
    if (reEnterRoutingError) errors.reEnterRoutingNumber = reEnterRoutingError;

    return errors;
}

export const BankAccountsOverview: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [formData, setFormData] = useState({
        accountType: '',
        nickName: '',
        bankName: '',
        routingNumber: '',
        reEnterRoutingNumber: '',
        accountNumber: '',
        reEnterAccountNumber: '',
        accountId: '',
    });
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const { contracts: contractsFromStore } = useSelector((state: RootState) => state.contracts);
    const [openInvoices, setOpenInvoices] = useState<OpenInvoiceApiResponse[]>([]);
    const [isOpenInvoicesLoading, setIsOpenInvoicesLoading] = useState(true);

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [country, setCountry] = useState('');
    // State for showing/hiding sensitive fields
    const [showAccountNumber, setShowAccountNumber] = useState(false);
    const [showReEnterAccountNumber, setShowReEnterAccountNumber] = useState(false);
    const [showRoutingNumber, setShowRoutingNumber] = useState(false);
    const [showReEnterRoutingNumber, setShowReEnterRoutingNumber] = useState(false);
    const [makePaymentModalOpen, setMakePaymentModalOpen] = useState(false);
    const [selectedPaymentContracts, setSelectedPaymentContracts] = useState<string[]>([]);
    const [activeContracts, setActiveContracts] = useState<ContractApiResponse[]>([]);
    const [toggleConfirmModalOpen, setToggleConfirmModalOpen] = useState(false);
    const [pendingToggleAccount, setPendingToggleAccount] = useState<BankAccount | null>(null);
    const [isMovingToInactive, setIsMovingToInactive] = useState(true);
    const [aspireInactiveModalOpen, setAspireInactiveModalOpen] = useState(false);
    const [autoPayModalOpen, setAutoPayModalOpen] = useState(false);
    const [addPaymentMethodModalOpen, setAddPaymentMethodModalOpen] = useState(false);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
    const [savedCards, setSavedCards] = useState<StripePaymentMethodItem[]>([]);
    const [savedCardsLoading, setSavedCardsLoading] = useState(false);
    const accountTypeOptions = ['Checking', 'Savings'];
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const isStripeEnabled = brandDetails?.IsCreditPaymentEnabled === 1 || process.env.NODE_ENV === 'development';
    const initialDataFetch = useRef(false);

    const allContractsDisabled = calculateAllContractsDisabled(isOpenInvoicesLoading, activeContracts, openInvoices);

    const isAddAccountFormValid = useMemo(
        () => Object.keys(computeAddAccountFormErrors(formData, country, t)).length === 0,
        [formData, country, t],
    );

    const getBankAccounts = useCallback(async () => {
        try {
            const response = await getBankAccountsList();
            if (response?.statusCode === 200) {
                setBankAccounts(
                    response?.data?.map((bankAccount: BankAccountApiResponse) => ({
                        id: bankAccount?.Id,
                        accountType: bankAccount?.AccountType || bankAccount?.accountType || '—',
                        nickName: bankAccount?.NickName || bankAccount?.nickName || bankAccount?.accountNickname || '',
                        bankName: bankAccount?.BankName,
                        routingNumber: bankAccount?.IdentificationNumber,
                        accountNumber: bankAccount?.AccountNumber,
                        accountId: bankAccount?.AspireEntityOId || bankAccount?.AspireRecordId || '',
                        IsActive: bankAccount?.IsActive,
                        AspireIsActive: bankAccount?.AspireIsActive,
                        AutoPay: bankAccount?.AutoPay,
                    })),
                );
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: t('BankAccounts.errors.fetchBankAccountsFailed'),
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, t]);

    useEffect(() => {
        if (contractsFromStore && Array.isArray(contractsFromStore)) {
            const filtered = contractsFromStore?.filter(
                (contract: ContractApiResponse) => contract?.Status === 1 || !contract?.Status,
            );
            setActiveContracts(filtered);
        }
    }, [contractsFromStore]);

    useEffect(() => {
        if (initialDataFetch.current) return;
        initialDataFetch.current = true;

        getBankAccounts();
        // Fetch open invoices for Pay All button logic.
        // isOpenInvoicesLoading starts true so the button isn't incorrectly disabled
        // during the async fetch (openInvoices is [] until the response arrives).
        (async () => {
            try {
                const response = await getOpenInvoices(false);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    setOpenInvoices(response.data);
                } else {
                    setOpenInvoices([]);
                }
            } catch {
                setOpenInvoices([]);
            } finally {
                setIsOpenInvoicesLoading(false);
            }
        })();
    }, [getBankAccounts]);

    const handleCloseMakePaymentModal = () => {
        setMakePaymentModalOpen(false);
        setSelectedPaymentContracts([]);
    };

    const handleAddPayment = () => {
        if (selectedPaymentContracts?.length > 0) {
            navigate(NavigationConfig?.DashboardMakePayment, {
                state: { selectedContractIds: selectedPaymentContracts },
            });
            handleCloseMakePaymentModal();
        }
    };

    const fetchSavedCards = async () => {
        setSavedCardsLoading(true);
        try {
            const response = await getStripePaymentMethods(false, { type: 'card' });
            if (response?.statusCode === 200 && Array.isArray(response.data)) {
                setSavedCards(response.data);
            } else {
                setSavedCards([]);
            }
        } catch (error) {
            console.error('Error fetching saved cards:', error);
            setSavedCards([]);
        } finally {
            setSavedCardsLoading(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        // Bank name: allow only safe characters (no HTML, scripts, URLs)
        if (field === 'bankName' || field === 'nickName') {
            value = sanitizeBankNameInput(value);
        }
        // Account number fields: alphanumeric only, max 17 characters
        if (field === 'accountNumber' || field === 'reEnterAccountNumber') {
            value = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, ACCOUNT_NUMBER_MAX_LENGTH);
        }
        // Routing number fields: numeric only, max 9 digits
        if (field === 'routingNumber' || field === 'reEnterRoutingNumber') {
            value = value.replace(/\D/g, '');
        }
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handleAddAccount = async () => {
        const errors = computeAddAccountFormErrors(formData, country, t);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            // Call API to create bank account
            const payload = {
                bankName: formData.bankName.trim(),
                accountNumber: formData.accountNumber.trim(),
                country: getCountryCode(country),
                accountType: formData.accountType,
                routingNumber: formData.routingNumber.trim(),
                nickName: formData.nickName.trim() || null,
            };

            const response = await createBankAccount(payload, true);

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Show success message
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('BankAccounts.success.accountAdded'),
                        severity: 'success',
                    }),
                );

                // Reset form and close drawer
                setFormData({
                    accountType: '',
                    nickName: '',
                    bankName: '',
                    routingNumber: '',
                    reEnterRoutingNumber: '',
                    accountNumber: '',
                    reEnterAccountNumber: '',
                    accountId: '',
                });
                setCountry('');
                setFormErrors({});
                setDrawerOpen(false);

                // Refresh bank accounts list
                await getBankAccounts();
            } else {
                // Show error message
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('BankAccounts.errors.addBankAccountFailed'),
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            console.error('Error creating bank account:', error);
            const e = error as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e?.message || t('BankAccounts.errors.addBankAccountFailed'),
                    severity: 'error',
                }),
            );
        }
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setCountry('');
        setFormData({
            accountType: '',
            nickName: '',
            bankName: '',
            routingNumber: '',
            reEnterRoutingNumber: '',
            accountNumber: '',
            reEnterAccountNumber: '',
            accountId: '',
        });
        setFormErrors({});
        // Reset visibility states
        setShowAccountNumber(false);
        setShowReEnterAccountNumber(false);
        setShowRoutingNumber(false);
        setShowReEnterRoutingNumber(false);
    };

    const handleToggleAccountStatus = (e: React.SyntheticEvent, account: BankAccount) => {
        e.stopPropagation();

        // If account has AutoPay enabled, show AutoPay modal and prevent deactivation
        if (account.AutoPay === true) {
            setPendingToggleAccount(account);
            setAutoPayModalOpen(true);
            return;
        }

        // Otherwise, proceed with normal toggle confirmation
        setPendingToggleAccount(account);
        setIsMovingToInactive(account.IsActive === true);
        setToggleConfirmModalOpen(true);
    };

    const handleCloseToggleConfirmModal = () => {
        setToggleConfirmModalOpen(false);
        setPendingToggleAccount(null);
    };

    const showToggleStatusToast = (message: string, severity: 'success' | 'error') => {
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message,
                severity,
            }),
        );
    };

    const trackBankAccountToggle = (newStatus: boolean, bankName: string) => {
        trackBankAccountAction(newStatus ? 'activated' : 'deactivated', 'bank_accounts_page', bankName);
    };

    const handleConfirmToggle = async () => {
        if (!pendingToggleAccount) return;
        const newStatus = !pendingToggleAccount.IsActive;
        const toggleAction = newStatus ? 'activate' : 'deactivate';
        const bankName = pendingToggleAccount.bankName;

        try {
            const response = await updateBankAccountStatus(pendingToggleAccount.id, newStatus, true);

            if (response.statusCode !== 200 && response.statusCode !== 201) {
                trackBankAccountActionFailed(toggleAction, response.message, 'bank_accounts_page', bankName);
                showToggleStatusToast(response.message || t('BankAccounts.errors.updateStatusFailed'), 'error');
                handleCloseToggleConfirmModal();
                return;
            }

            trackBankAccountToggle(newStatus, bankName);
            showToggleStatusToast(
                newStatus ? t('BankAccounts.success.accountActivated') : t('BankAccounts.success.accountDeactivated'),
                'success',
            );
            setBankAccounts(prev =>
                prev.map(acc => (acc.id === pendingToggleAccount.id ? { ...acc, IsActive: newStatus } : acc)),
            );
            handleCloseToggleConfirmModal();
        } catch (error) {
            console.error('Error updating account status:', error);
            const e = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage =
                e?.response?.data?.message || e?.message || t('BankAccounts.errors.updateStatusFailed');
            trackBankAccountActionFailed(toggleAction, errorMessage, 'bank_accounts_page', bankName);
            showToggleStatusToast(errorMessage, 'error');
        }
    };

    const activeAccountsList = bankAccounts.filter(
        account => account?.IsActive === true && account?.AspireIsActive !== false,
    );
    const inactiveAccountsList = bankAccounts.filter(
        account => account?.IsActive === false || account?.AspireIsActive === false,
    );

    const formatCardExpiry = (month?: number, year?: number) => {
        if (month != null && year != null) {
            const mm = month < 10 ? `0${month}` : String(month);
            const yy = year >= 2000 ? String(year).slice(-2) : String(year);
            return `${mm}/${yy}`;
        }
        return '—';
    };

    const renderExpiredBadge = (expiryMonth?: number, expiryYear?: number) => {
        if (!expiryMonth || !expiryYear) return null;
        const now = new Date();
        const expiryDate = new Date(Number(expiryYear), Number(expiryMonth), 0, 23, 59, 59, 999);
        if (expiryDate >= now) return null;

        return (
            <Box
                component="span"
                sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.errorPreset[10],
                    bgcolor: colors.errorPreset[20],
                    border: `1px solid ${colors.errorPreset[30]}`,
                }}
            >
                {t('makePayment.expired')}
            </Box>
        );
    };

    const renderCardsList = () => (
        <>
            <BankAccountsContainer>
                {savedCardsLoading ? (
                    <Stack width="100%" alignItems="center" py={6}>
                        <Typography color="textSecondary">{t('addPaymentMethodModal.loadingPaymentForm')}</Typography>
                    </Stack>
                ) : (
                    savedCards.length > 0 &&
                    savedCards.map(card => (
                        <BankAccountCardWrapper key={card.id}>
                            <Box className="bank-icon-wrapper">
                                <Box className="bank-icon-name-wrapper">
                                    <span className="icon-outline-card" style={{ fontSize: 24 }} />
                                    <Box className="bankContent">
                                        <Typography variant="caption" className="info-label">
                                            {t('makePayment.paymentCard')}
                                        </Typography>
                                        <Typography variant="subtitle1" noWrap className="bank-name-text">
                                            {card.label}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Stack className="account-info-container">
                                <Box>
                                    <Typography variant="caption" className="info-label">
                                        {t('BankAccounts.expires')}
                                    </Typography>
                                    <Typography variant="subtitle1" className="info-value">
                                        {formatCardExpiry(card.expiryMonth, card.expiryYear)}
                                        {renderExpiredBadge(card.expiryMonth, card.expiryYear)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </BankAccountCardWrapper>
                    ))
                )}
            </BankAccountsContainer>
            {!savedCardsLoading && savedCards.length === 0 && (
                <Stack height={'100%'}>
                    <Stack justifyContent="center" alignItems="center" sx={EMPTY_STATE_OVERLAY_SX}>
                        <NoBankAccount />
                        <Typography variant="h6" mt={1.5} color="textSecondary" textAlign={'center'}>
                            {t('BankAccounts.noCardsFound')}
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </>
    );

    const renderBankAccountCard = (account: BankAccount) => (
        <BankAccountCard
            key={account.id}
            id={account.id}
            accountType={account.accountType}
            nickName={account.nickName}
            bankName={account.bankName}
            routingNumber={account.routingNumber}
            accountNumber={account.accountNumber}
            IsActive={account.IsActive}
            AspireIsActive={account.AspireIsActive}
            AutoPay={account.AutoPay}
            isImpersonating={impersonation?.isImpersonating}
            onToggleStatus={accountId => {
                const acc = bankAccounts.find(a => a.id === accountId);
                if (acc) {
                    handleToggleAccountStatus({ stopPropagation: () => {} } as React.SyntheticEvent, acc);
                }
            }}
            onAspireInactiveClick={() => setAspireInactiveModalOpen(true)}
            onUnmaskError={() =>
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('BankAccounts.errors.unmaskAccountNumberFailed'),
                        severity: 'error',
                    }),
                )
            }
        />
    );

    const renderAccountsList = (accounts: BankAccount[], isActiveTab: boolean) => {
        const emptyLabel = isActiveTab
            ? t('BankAccounts.list.noBankAccountsFound')
            : t('BankAccounts.list.inactiveBankAccounts');
        return (
            <>
                <BankAccountsContainer>
                    {accounts?.length > 0 && accounts.map(renderBankAccountCard)}
                </BankAccountsContainer>
                {accounts?.length === 0 && !isLoading && (
                    <Stack height={'100%'}>
                        <Stack justifyContent="center" alignItems="center" sx={EMPTY_STATE_OVERLAY_SX}>
                            <NoBankAccount />
                            <Typography
                                variant="h6"
                                component={'h1'}
                                mt={1.5}
                                color="textSecondary"
                                textAlign={'center'}
                            >
                                {emptyLabel}
                            </Typography>
                        </Stack>
                    </Stack>
                )}
            </>
        );
    };

    const handlePayAll = () => {
        if (activeContracts?.length > 0) {
            const allContractIds = activeContracts.map(
                contractItem => contractItem.AspireId || `Contract ${contractItem.Id}`,
            );
            navigate(NavigationConfig.DashboardMakePayment, {
                state: { selectedContractIds: allContractIds, payAll: true, fromBankAccounts: true },
            });
        }
    };

    useEffect(() => {
        fetchSavedCards();
    }, []);

    return (
        <Stack gap={3} p={3}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={1.5}>
                <CommonHeader
                    mainTitle={t('BankAccounts.header.mainTitle')}
                    subTitle={t('BankAccounts.header.subTitle')}
                    icon="icon-user-square"
                />
                <Stack direction={'row'} gap={2} width={{ xs: '100%', sm: 'auto' }} justifyContent={'end'}>
                    {!impersonation?.isImpersonating && (
                        <ButtonComponent
                            variant="outlined"
                            onClick={() => {
                                setDrawerOpen(true);
                            }}
                            startIcon={<span className="icon-plus" />}
                            sx={{
                                width: 'fit-content',
                            }}
                        >
                            {t('BankAccounts.addAccount')}
                        </ButtonComponent>
                    )}
                    {isStripeEnabled && !impersonation?.isImpersonating && (
                        <ButtonComponent
                            variant="outlined"
                            onClick={async () => {
                                try {
                                    const response = await createStripeSetupIntent(true);
                                    const secret = response?.clientSecret ?? response?.data?.clientSecret;
                                    if (secret) {
                                        setSetupIntentClientSecret(secret);
                                        setAddPaymentMethodModalOpen(true);
                                    } else {
                                        dispatch(
                                            openToast({
                                                show: true,
                                                isAlertOpen: true,
                                                message: t('makePayment.couldNotLoadPaymentForm'),
                                                severity: 'error',
                                            }),
                                        );
                                    }
                                } catch (error) {
                                    console.error('SetupIntent API error:', error);
                                    dispatch(
                                        openToast({
                                            show: true,
                                            isAlertOpen: true,
                                            message: t('makePayment.couldNotStartAddCard'),
                                            severity: 'error',
                                        }),
                                    );
                                }
                            }}
                            startIcon={<span className="icon-plus" />}
                            sx={{
                                width: 'fit-content',
                            }}
                        >
                            {t('BankAccounts.addCards')}
                        </ButtonComponent>
                    )}
                    <ButtonComponent
                        variant="contained"
                        sx={{
                            width: 'fit-content',
                        }}
                        onClick={handlePayAll}
                        disabled={allContractsDisabled}
                    >
                        {t('dashboard.header.makePayment')}
                    </ButtonComponent>
                </Stack>
            </Stack>

            {/* Tabs for Active and Inactive Accounts */}
            <CommonTabs
                tabs={[
                    {
                        label: `Active (${activeAccountsList.length})`,
                        content: renderAccountsList(activeAccountsList, true),
                    },
                    {
                        label: `Inactive (${inactiveAccountsList.length})`,
                        content: renderAccountsList(inactiveAccountsList, false),
                    },
                    ...(isStripeEnabled
                        ? [
                              {
                                  label: `${t('BankAccounts.cardsTabLabel')} (${savedCards.length})`,
                                  content: renderCardsList(),
                              },
                          ]
                        : []),
                ]}
            />

            <AddPaymentMethodModal
                open={addPaymentMethodModalOpen}
                clientSecret={setupIntentClientSecret}
                onClose={() => {
                    setAddPaymentMethodModalOpen(false);
                    setSetupIntentClientSecret(null);
                }}
                onSuccess={() => {
                    setAddPaymentMethodModalOpen(false);
                    setSetupIntentClientSecret(null);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('BankAccounts.paymentMethodSavedSuccess'),
                            severity: 'success',
                        }),
                    );
                    fetchSavedCards();
                }}
            />

            {/* Add Account Drawer */}
            <AddBankAccountDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                formData={formData}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                country={country}
                setCountry={setCountry}
                onInputChange={handleInputChange}
                onAddAccount={handleAddAccount}
                accountTypeOptions={accountTypeOptions}
                showAccountNumber={showAccountNumber}
                setShowAccountNumber={setShowAccountNumber}
                showReEnterAccountNumber={showReEnterAccountNumber}
                setShowReEnterAccountNumber={setShowReEnterAccountNumber}
                showRoutingNumber={showRoutingNumber}
                setShowRoutingNumber={setShowRoutingNumber}
                showReEnterRoutingNumber={showReEnterRoutingNumber}
                setShowReEnterRoutingNumber={setShowReEnterRoutingNumber}
                addAccountDisabled={!isAddAccountFormValid}
                accountTypeAriaLabel={t('payments.createPayment.country')}
                accountTypePlaceholder={t('BankAccounts.modal.selectAccountTypePlaceholder')}
                routingNumberPreventCopy={false}
                accountNumberPreventCopy={false}
            />

            {/* Make Payment Modal */}
            <CommonModal
                modalOpen={makePaymentModalOpen}
                onCloseModal={handleCloseMakePaymentModal}
                DialogMainTitle={t('payments.createPayment.title')}
                showPrimaryButton={false}
                secondaryBtnName={'Add'}
                secondaryBtnClick={handleAddPayment}
                secondaryBtnDisabled={selectedPaymentContracts.length === 0}
                ModalMaxWidth={600}
            >
                <CustomMultiSelect
                    value={selectedPaymentContracts}
                    onChange={setSelectedPaymentContracts}
                    options={activeContracts.map(
                        contractItem => contractItem.AspireId || `Contract ${contractItem.Id}`,
                    )}
                    title={t('payments.createPayment.selectContract')}
                    placeholder={t('payments.createPayment.selectContractId')}
                    required
                    maxHeight={250}
                    searchable
                    showSelectAll
                />
            </CommonModal>

            {/* Aspire Inactive — Contact Support Modal */}
            <CommonModal
                modalOpen={aspireInactiveModalOpen}
                onCloseModal={() => setAspireInactiveModalOpen(false)}
                DialogMainTitle={t('registration.contactSupport.title')}
                showPrimaryButton={false}
                secondaryBtnName={t('BankAccounts.modal.gotIt')}
                secondaryBtnClick={() => setAspireInactiveModalOpen(false)}
                ModalMaxWidth={400}
            >
                <ContactSupportModalContent introText={t('BankAccounts.bankAccountDeactivation')} />
            </CommonModal>

            {/* AutoPay Active — Cannot Deactivate Modal */}
            <CommonModal
                modalOpen={autoPayModalOpen}
                onCloseModal={() => setAutoPayModalOpen(false)}
                DialogMainTitle={t('BankAccounts.modal.autoPayActiveTitle')}
                showPrimaryButton={false}
                secondaryBtnName={t('BankAccounts.modal.gotIt')}
                secondaryBtnClick={() => setAutoPayModalOpen(false)}
                ModalMaxWidth={400}
            >
                <ContactSupportModalContent introText="This bank account has an active AutoPay setup and cannot be deactivated. Please contact Customer Support for assistance." />
            </CommonModal>

            {/* Active Inactive Confirmation Modal */}
            <CommonModal
                modalOpen={toggleConfirmModalOpen}
                onCloseModal={handleCloseToggleConfirmModal}
                DialogMainTitle={getToggleModalTitle(isMovingToInactive, t)}
                primaryBtnName={t('BankAccounts.No')}
                // secondaryBtnName={isMovingToInactive ? t('BankAccounts.moveToInactive') : t('BankAccounts.moveToActive')}
                secondaryBtnName={t('BankAccounts.Yes')}
                primaryBtnClick={handleCloseToggleConfirmModal}
                secondaryBtnClick={handleConfirmToggle}
                ModalMaxWidth={400}
            >
                <Box>
                    <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                        {getToggleModalBodyText(isMovingToInactive)}
                    </Typography>

                    {/* {pendingToggleAccount && (
                        <Box
                            display={'flex'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            sx={{ p: 2, backgroundColor: colors.greyPreset[90], borderRadius: 2 }}
                        >
                            <Stack>
                                <Typography variant="caption" sx={{ color: colors.greyPreset[100], mb: 0.5 }}>
                                    Bank Name
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.fullBlack, mb: 1.5 }}>
                                    {pendingToggleAccount?.bankName}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography variant="caption" sx={{ color: colors.greyPreset[100], mb: 0.5 }}>
                                    Account ID
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.fullBlack }}>
                                    {pendingToggleAccount?.accountId}
                                </Typography>
                            </Stack>
                        </Box>
                    )} */}
                </Box>
            </CommonModal>
        </Stack>
    );
};
